import { useState, useMemo } from "react";
import { PhysShell, Field, ResultCard, Stat, primaryBtn, PHYS_ACCENT } from "./PhysShell.jsx";
import { projectile, projectileAt, round } from "./physcore.js";

export default function Projectile() {
  const [v0, setV0] = useState("20");
  const [ang, setAng] = useState("45");
  const [h0, setH0] = useState("0");

  const data = useMemo(() => {
    const V = parseFloat(v0), A = parseFloat(ang), H = parseFloat(h0);
    if ([V, A, H].some((x) => isNaN(x))) return { err: "Enter numeric values" };
    const r = projectile(V, A, H);
    if (!r.ok) return { err: r.error };
    // Sample trajectory for the plot
    const N = 60;
    const pts = [];
    for (let i = 0; i <= N; i++) {
      const t = (i / N) * r.timeOfFlight;
      const p = projectileAt(V, A, t, H);
      pts.push([p.x, Math.max(0, p.y)]);
    }
    return { r, pts };
  }, [v0, ang, h0]);

  return (
    <PhysShell title="Projectile Motion">
      <p style={{ fontSize: 13, color: "#5B5E7A", marginTop: 0 }}>
        Ideal model — no air resistance, g = 9.81 m/s².
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Initial speed v₀" suffix="m/s" value={v0} onChange={(e) => setV0(e.target.value)} />
        <Field label="Launch angle" suffix="°" value={ang} onChange={(e) => setAng(e.target.value)} />
      </div>
      <Field label="Initial height h₀" suffix="m" value={h0} onChange={(e) => setH0(e.target.value)} />

      {data.err ? (
        <ResultCard><span style={{ color: "#F8716A" }}>{data.err}</span></ResultCard>
      ) : (
        <>
          <TrajectoryPlot pts={data.pts} />
          <ResultCard>
            <Stat label="Range" value={round(data.r.range, 3)} unit="m" />
            <Stat label="Max height" value={round(data.r.maxHeight, 3)} unit="m" />
            <Stat label="Time of flight" value={round(data.r.timeOfFlight, 3)} unit="s" />
            <Stat label="Time to apex" value={round(data.r.timeToApex, 3)} unit="s" />
            <Stat label="Horizontal velocity vₓ" value={round(data.r.vx, 3)} unit="m/s" />
            <Stat label="Impact speed" value={round(data.r.impactSpeed, 3)} unit="m/s" />
          </ResultCard>
        </>
      )}
    </PhysShell>
  );
}

function TrajectoryPlot({ pts }) {
  const W = 320, H = 200, pad = 28;
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const maxX = Math.max(...xs, 1);
  const maxY = Math.max(...ys, 1);
  const sx = (x) => pad + (x / maxX) * (W - 2 * pad);
  const sy = (y) => H - pad - (y / maxY) * (H - 2 * pad);
  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${sx(p[0]).toFixed(1)},${sy(p[1]).toFixed(1)}`).join(" ");
  const apexIdx = ys.indexOf(maxY);

  return (
    <div style={{ marginTop: 18, background: "#13141F", border: "1px solid #1C1E2E", borderRadius: 12, padding: 12 }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
        <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} stroke="#2A2C3D" />
        <line x1={pad} y1={pad} x2={pad} y2={H - pad} stroke="#2A2C3D" />
        <path d={path} fill="none" stroke={PHYS_ACCENT} strokeWidth="2.5" />
        <circle cx={sx(pts[apexIdx][0])} cy={sy(pts[apexIdx][1])} r="4" fill={PHYS_ACCENT} />
        <circle cx={sx(pts[pts.length - 1][0])} cy={sy(0)} r="4" fill="#F8716A" />
        <text x={W - pad} y={H - pad + 16} fill="#5B5E7A" fontSize="10" textAnchor="end">{round(maxX, 1)} m</text>
        <text x={pad - 6} y={pad + 4} fill="#5B5E7A" fontSize="10" textAnchor="end">{round(maxY, 1)} m</text>
      </svg>
    </div>
  );
}

